import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAccount, useSignMessage } from 'wagmi'
import {
  fetchNonce,
  logout as apiLogout,
  refreshSession,
  setAccessToken,
  verifySignature,
} from '@/lib/api'
import { fallbackChainId } from '@/lib/wagmi'

export interface Session {
  address: string
  /**
   * Whether this address is in the API's ADMIN_ADDRESSES.
   *
   * Everything in the panel hangs off this, but it is a HINT FOR RENDERING, not the
   * control. Every admin endpoint re-checks it server-side, so a tampered `true` here
   * buys a nicer-looking 403 and nothing else.
   */
  isAdmin: boolean
}

/**
 * SIWE session management for the operator panel.
 *
 * The access token lives in memory only. On load we attempt a silent refresh against
 * the httpOnly cookie, so a reload restores the session without another signature
 * while an XSS payload still cannot read the token. A refresh is scheduled at 80% of
 * the token lifetime so a request never races an expiry.
 */
export function useAuth() {
  const { address, isConnected, isReconnecting, chainId } = useAccount()
  const { signMessageAsync } = useSignMessage()
  const queryClient = useQueryClient()

  const [session, setSession] = useState<Session | null>(null)
  const [restoring, setRestoring] = useState(true)

  // Silent restore on mount.
  useEffect(() => {
    let cancelled = false
    refreshSession()
      .then((result) => {
        if (cancelled) return
        setAccessToken(result.accessToken)
        setSession({ address: result.address, isAdmin: result.isAdmin })
      })
      .catch(() => {
        // No valid cookie — nobody is signed in yet. Not an error.
      })
      .finally(() => !cancelled && setRestoring(false))

    return () => {
      cancelled = true
    }
  }, [])

  // Proactive rotation at 12 minutes of a 15-minute token.
  useEffect(() => {
    if (!session) return
    const id = setInterval(
      () => {
        refreshSession()
          .then((result) => {
            setAccessToken(result.accessToken)
            setSession({ address: result.address, isAdmin: result.isAdmin })
          })
          .catch(() => {
            setAccessToken(null)
            setSession(null)
          })
      },
      12 * 60 * 1000,
    )
    return () => clearInterval(id)
  }, [session])

  /**
   * The session is bound to one address and must be torn down when that binding breaks
   * — the user switched account in their wallet, or disconnected from the wallet's own
   * UI rather than ours.
   *
   * `isReconnecting` is excluded deliberately: during rehydration the address is
   * momentarily undefined, and treating that as a disconnect would sign the operator
   * out on every page load.
   */
  useEffect(() => {
    if (!session || isReconnecting) return

    const disconnected = !isConnected || !address
    const switchedAccount = !!address && address.toLowerCase() !== session.address.toLowerCase()

    if (disconnected || switchedAccount) {
      setAccessToken(null)
      setSession(null)
      // Best-effort cookie clear, so the next person at this machine cannot silently
      // resume an operator session from the refresh cookie.
      void apiLogout().catch(() => {})
      void queryClient.invalidateQueries()
    }
  }, [address, isConnected, isReconnecting, session, queryClient])

  const signIn = useMutation({
    mutationFn: async () => {
      if (!address) throw new Error('Connect a wallet first.')

      const { nonce, domain, statement } = await fetchNonce()

      // EIP-4361, assembled to match exactly what the server verifies.
      const message = [
        `${domain} wants you to sign in with your Ethereum account:`,
        address,
        '',
        statement,
        '',
        `URI: ${window.location.origin}`,
        'Version: 1',
        // The chain the wallet is ACTUALLY on. The message is a signed statement about
        // where the signer is; a hardcoded default would make it a false one.
        `Chain ID: ${chainId ?? fallbackChainId}`,
        `Nonce: ${nonce}`,
        `Issued At: ${new Date().toISOString()}`,
      ].join('\n')

      const signature = await signMessageAsync({ message })
      const result = await verifySignature(message, signature)

      setAccessToken(result.accessToken)
      return { address: result.address, isAdmin: result.isAdmin }
    },
    onSuccess: (result) => {
      setSession(result)
      void queryClient.invalidateQueries()
    },
  })

  /**
   * Attempt sign-in automatically once a wallet connects.
   *
   * `attempted` is a ref, not state, so recording an attempt does not re-render and
   * re-run this effect. An operator who dismisses the wallet prompt is not asked again
   * on the next render — which, without this, is an unescapable loop of signature
   * popups. The manual button is how someone who declined gets a second chance.
   */
  const attempted = useRef<Set<string>>(new Set())

  useEffect(() => {
    // `restoring` matters: the silent refresh may be about to produce a session from
    // the cookie, and prompting over the top of it asks for a signature nobody needed.
    if (restoring || isReconnecting) return
    if (!isConnected || !address) return
    if (session || signIn.isPending) return

    const key = address.toLowerCase()
    if (attempted.current.has(key)) return
    attempted.current.add(key)

    // Rejection is a decision, not an error.
    void signIn.mutateAsync().catch(() => {})
    // `signIn` is a stable mutation object; including it would re-run this on every
    // status change, including the one this effect itself causes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, isConnected, isReconnecting, restoring, session])

  // Forget the attempt on disconnect, so reconnecting prompts again.
  useEffect(() => {
    if (!isConnected) attempted.current.clear()
  }, [isConnected])

  const signOut = useCallback(async () => {
    await apiLogout().catch(() => setAccessToken(null))
    setSession(null)
    await queryClient.invalidateQueries()
  }, [queryClient])

  return {
    session,
    restoring,
    isSignedIn: session !== null,
    /** Signed in AND on the admin list. What every page in the panel gates on. */
    isAdmin: session?.isAdmin === true,
    /**
     * Signed in but NOT on the admin list. Distinct from "not signed in", and worth
     * its own screen: the wallet proved itself and was still turned away, so the fix is
     * to switch accounts, not to sign again.
     */
    isSignedInNotAdmin: session !== null && !session.isAdmin,
    canSignIn: isConnected && session === null && !restoring,
    signIn: signIn.mutateAsync,
    signingIn: signIn.isPending,
    signInError: signIn.error,
    signOut,
  }
}

/** Turns a sign-in failure into something an operator can act on. */
export function signInFailureReason(error: Error): string {
  const m = error.message
  if (/user rejected|user denied|signature.*reject/i.test(m)) return 'Signature rejected.'
  if (/failed to fetch|networkerror|load failed/i.test(m))
    return 'Cannot reach the API — check that the backend is running and that this origin is in CORS_ORIGINS.'
  if (/expired|no longer valid|already used/i.test(m)) return 'Request expired — try again.'
  return 'Sign-in failed.'
}
